import React from 'react';
import classNames from 'classnames';
import { Card, Col, Row } from 'react-bootstrap';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import FormattedCurrency from '../../../../../components/currency/FormattedCurrency';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { computeResults } from '../logic';
import { CompensationReviewItem } from '../types';

type Props = {
  items: CompensationReviewItem[];
  currency: string;
};

// Results panel: reference vs new totals and the net change, all normalised to
// monthly equivalents (mixed pay frequencies are converted to a monthly figure).
export const CompensationReviewResults: React.FC<
  React.PropsWithChildren<Props>
> = ({ items, currency }) => {
  const t = useNamespacedTranslation('compensationReviews.results');
  const {
    totalReferenceMonthly,
    totalNewMonthly,
    netChangeAbsolute,
    netChangePercent,
  } = computeResults(items);

  const hasIncrease = netChangeAbsolute > 0;
  const hasDecrease = netChangeAbsolute < 0;
  // The new total is a neutral figure (a lower salary total is not an error), so
  // it keeps the default emphasis. Only the net change carries the up/down
  // colour, since that is where direction is the point.
  const valueClassName = 'compensation-results__value mb-0';
  const changeClassName = classNames(valueClassName, {
    'text-success': hasIncrease,
    'text-danger': hasDecrease,
  });
  const trendIcon = hasDecrease ? 'caret-down-outline' : 'caret-up-outline';

  return (
    <Card className="flex-grow-1">
      <Card.Body className="d-flex flex-column">
        <div className="d-flex align-items-center mb-4">
          <FlairIcon icon="phosphor-trend-up" className="me-2" />
          <h4 className="mb-0">{t('title')}</h4>
        </div>

        {/* Spread the two metrics across the card width and centre them
            vertically so the panel fills the column instead of leaving a large
            empty area below a short left-aligned block. */}
        <Row className="g-4 flex-grow-1 align-items-center">
          <Col md={6}>
            <div className="text-muted small mb-2">{t('total')}</div>
            <div className="d-flex align-items-center flex-wrap">
              <span className="fs-4 text-muted">
                <FormattedCurrency
                  value={totalReferenceMonthly}
                  currency={currency}
                />
              </span>
              <FlairIcon
                icon="arrow-forward-outline"
                className="mx-2 text-muted"
              />
              <span className={valueClassName}>
                <FormattedCurrency
                  value={totalNewMonthly}
                  currency={currency}
                />
              </span>
            </div>
          </Col>

          <Col md={6} className="compensation-results__net-change">
            <div className="text-muted small mb-2">{t('netChange')}</div>
            <div className="d-flex align-items-center">
              {(hasIncrease || hasDecrease) && (
                <FlairIcon
                  icon={trendIcon}
                  className={classNames('me-2', {
                    'text-success': hasIncrease,
                    'text-danger': hasDecrease,
                  })}
                />
              )}
              <span className={changeClassName}>
                <FormattedCurrency
                  value={netChangeAbsolute}
                  currency={currency}
                />
              </span>
              <span className="ms-2 fs-5 text-muted">
                {netChangePercent.toFixed(2)}%
              </span>
            </div>
          </Col>
        </Row>

        <div className="text-muted small mt-4">{t('monthlyEquivalents')}</div>
      </Card.Body>
    </Card>
  );
};
